<template lang="pug">
b-container.dsg-page(fluid)
    .dsg-page-header
        div
            h1.dsg-page-title
                Icon(icon="mdi:cash-multiple")
                | Programme Fees
            p.dsg-page-subtitle Create subsidy templates and bulk-import fee tiers from Excel

    //---------- Existing fees ----------//
    .dsg-card.mb-4
        .dsg-card-header.d-flex.align-items-center.justify-content-between
            div
                h2.dsg-card-title Existing programme fees
                p.dsg-card-sub Templates currently in the fee table
            .d-flex.align-items-center(style="gap: 0.75rem;")
                b-button.btn-dsg-outline(size="sm" @click="exportToExcel" :disabled="!fees.length")
                    Icon.mr-1(icon="mdi:file-excel-outline")
                    | Export to Excel
                b-badge.dsg-count-pill(pill) {{ fees.length }}
        .dsg-card-pad
            .dsg-table-wrap
                b-table.dsg-table(:fields="feeTableFields" :items="fees" responsive hover show-empty)
                    template(#empty="scope")
                        .dsg-empty(v-if="loadingFees")
                            b-spinner(variant="primary")
                        .dsg-empty(v-else)
                            Icon.dsg-empty-icon(icon="mdi:cash-remove" width="48")
                            p.mb-0 No programme fees found
                    template(#cell(flags)="data")
                        span.dsg-tag(v-if="data.item.crb5c_for_citizen") SG
                        span.dsg-tag(v-if="data.item.crb5c_for_pr") PR
                        span.dsg-tag(v-if="data.item.crb5c_for_ltvp") LTVP
                        span.dsg-tag.dsg-tag-cip(v-if="data.item.crb5c_iscip2") CIP 2.0
                        span.dsg-tag(v-if="data.item.crb5c_born1969orbefore") ≤1969
                        span.dsg-tag.dsg-tag-transport(v-if="data.item.crb5c_transportfee") Transport
                    template(#cell(crb5c_feeactive)="data")
                        span.dsg-pill(:class="data.item.crb5c_feeactive ? 'dsg-pill-active' : 'dsg-pill-inactive'")
                            | {{ data.item.crb5c_feeactive ? 'Active' : 'Inactive' }}

    b-row
        //---------- Manual create ----------//
        b-col.col-12.col-xl-6.mb-4
            .dsg-card.h-100
                .dsg-card-header
                    h2.dsg-card-title Add a subsidy template
                    p.dsg-card-sub Fill the fields, then add it to the preview below
                .dsg-card-pad
                    b-row
                        b-col.col-12.col-md-6.mb-3(v-for="field in feeFields" :key="field.key")
                            .dsg-field-label {{ field.label }}
                                span.dsg-req(v-if="field.required")  *
                            b-form-input(
                                v-if="field.type === 'text'"
                                v-model="form[field.key]"
                                :placeholder="field.placeholder || ''"
                            )
                            b-form-input(
                                v-else-if="field.type === 'number'"
                                v-model="form[field.key]"
                                type="number"
                                step="0.01"
                                :placeholder="field.placeholder || '0'"
                            )
                            b-form-checkbox.dsg-switch(v-else v-model="form[field.key]" switch) {{ form[field.key] ? 'Yes' : 'No' }}
                    .d-flex.justify-content-end
                        b-button.btn-dsg(@click="addManualToPreview" :disabled="!form.crb5c_description")
                            Icon.mr-1(icon="mdi:plus")
                            | Add to preview

        //---------- Excel import ----------//
        b-col.col-12.col-xl-6.mb-4
            .dsg-card.h-100
                .dsg-card-header
                    h2.dsg-card-title Import from Excel
                    p.dsg-card-sub Upload a sheet; columns auto-map to fee fields
                .dsg-card-pad
                    b-form-file(
                        v-model="excelFile"
                        accept=".xlsx,.xls,.csv"
                        placeholder="Choose an Excel/CSV file…"
                        drop-placeholder="Drop file here…"
                        @input="onExcelFile"
                    )
                    .mt-2.text-muted.small(v-if="excelColumns.length")
                        Icon.mr-1(icon="mdi:table")
                        | {{ excelRows.length }} rows, {{ excelColumns.length }} columns detected

                    template(v-if="excelColumns.length")
                        .dsg-map-title.mt-3 Column mapping
                        p.text-muted.small.mb-2 Adjust any field that didn't auto-match.
                        .dsg-map-grid
                            .dsg-map-row(v-for="field in feeFields" :key="field.key")
                                span.dsg-map-label
                                    | {{ field.label }}
                                    span.dsg-req(v-if="field.required")  *
                                b-form-select.dsg-map-select(v-model="mapping[field.key]" :options="columnOptions" size="sm")
                        .d-flex.justify-content-end.mt-3
                            b-button.btn-dsg(@click="loadExcelRowsToPreview" :disabled="!mapping.crb5c_description")
                                Icon.mr-1(icon="mdi:table-arrow-down")
                                | Add {{ excelRows.length }} rows to preview

    //---------- Preview / staging ----------//
    .dsg-card.mb-4(v-if="draftRows.length")
        .dsg-card-header.d-flex.align-items-center.justify-content-between
            div
                h2.dsg-card-title Preview — to be added
                p.dsg-card-sub Pick the fields to upload, amend any values, tick rows, then confirm
            b-badge.dsg-count-pill(pill) {{ selectedValidCount }} selected
        .dsg-card-pad
            //- choose which fields to upload
            .dsg-include.mb-3
                span.dsg-include-label Fields to upload
                b-form-checkbox.dsg-include-chk(
                    v-for="field in feeFields"
                    :key="field.key"
                    v-model="include[field.key]"
                    :disabled="field.required"
                    inline
                ) {{ field.label }}
            //- editable preview grid
            .dsg-editable-wrap
                table.dsg-edit-table
                    thead
                        tr
                            th.dsg-th-tight
                                b-form-checkbox(v-model="allSelected" :indeterminate="someSelected")
                            th(v-for="field in includedFields" :key="field.key") {{ field.label }}
                            th.dsg-th-tight Status
                            th.dsg-th-tight
                    tbody
                        tr(v-for="(row, idx) in draftRows" :key="row._id" :class="{ 'dsg-row-invalid': !rowValid(row) }")
                            td
                                b-form-checkbox(v-model="row._selected" :disabled="!rowValid(row)")
                            td(v-for="field in includedFields" :key="field.key")
                                b-form-checkbox(v-if="field.type === 'bool'" v-model="row[field.key]" switch)
                                b-form-input.dsg-cell-input(
                                    v-else
                                    v-model="row[field.key]"
                                    :type="field.type === 'number' ? 'number' : 'text'"
                                    size="sm"
                                    :state="field.required && !row[field.key] ? false : null"
                                )
                            td
                                b-spinner(v-if="row._status === 'creating'" small)
                                span.dsg-pill.dsg-pill-active(v-else-if="row._status === 'done'") Created
                                span.dsg-pill.dsg-pill-inactive(v-else-if="row._status === 'error'" v-b-tooltip.hover :title="row._error") Failed
                                span.text-muted.small(v-else) Ready
                            td
                                b-button.dsg-icon-btn(variant="outline-danger" size="sm" @click="removeDraft(idx)")
                                    b-icon(icon="trash")
            .d-flex.justify-content-between.align-items-center.mt-3
                b-button(variant="light" size="sm" @click="clearDrafts") Clear all
                b-button.btn-dsg(@click="confirmCreate" :disabled="isSubmitting || selectedValidCount === 0")
                    b-spinner(v-if="isSubmitting" small)
                    span(v-else)
                        Icon.mr-1(icon="mdi:content-save-all-outline")
                        | Confirm &amp; create ({{ selectedValidCount }})
</template>

<script>
import { Icon } from '@iconify/vue2';
import * as XLSX from 'xlsx-js-style';

// Field metadata drives the form, the Excel auto-mapping and the payload.
const FEE_FIELDS = [
    { key: 'crb5c_description', label: 'Description', type: 'text', required: true, placeholder: 'e.g. CIP 2.0 SG born ≤1969', synonyms: ['description', 'desc', 'name', 'template', 'preset', 'tier'] },
    { key: 'crb5c_amt', label: 'Amount', type: 'number', synonyms: ['amount', 'amt', 'grossfee', 'fee', 'price', 'gross', 'monthlyfee'] },
    { key: 'crb5c_subsidy', label: 'Subsidy amount', type: 'number', synonyms: ['subsidyamount', 'subsidyamt', 'subsidyvalue', 'subsidydollar'] },
    { key: 'crb5c_subsidypercentage', label: 'Subsidy %', type: 'number', synonyms: ['subsidypercentage', 'subsidypercent', 'subsidyrate', 'percentage', 'percent', 'subsidy'] },
    { key: 'crb5c_type', label: 'Type', type: 'number', synonyms: ['type', 'category', 'code'] },
    { key: 'crb5c_transportamt', label: 'Transport amount', type: 'number', synonyms: ['transportamount', 'transportamt', 'transportfeeamount', 'transportcost'] },
    { key: 'crb5c_for_citizen', label: 'For Citizen (SG)', type: 'bool', synonyms: ['citizen', 'sg', 'singaporean', 'forcitizen', 'issg'] },
    { key: 'crb5c_for_pr', label: 'For PR', type: 'bool', synonyms: ['pr', 'permanentresident', 'forpr', 'ispr'] },
    { key: 'crb5c_for_ltvp', label: 'For LTVP', type: 'bool', synonyms: ['ltvp', 'longtermvisit', 'forltvp'] },
    { key: 'crb5c_iscip2', label: 'CIP 2.0', type: 'bool', synonyms: ['cip2', 'cip20', 'iscip2', 'cip2point0'] },
    { key: 'crb5c_born1969orbefore', label: 'Born 1969 or before', type: 'bool', synonyms: ['born1969orbefore', 'born1969', 'bornbefore1970', '1969', 'bornbefore'] },
    { key: 'crb5c_transportfee', label: 'Is transport fee', type: 'bool', synonyms: ['istransportfee', 'transportfee', 'transport'] },
    { key: 'crb5c_feeactive', label: 'Active', type: 'bool', synonyms: ['active', 'feeactive', 'enabled', 'isactive'] },
];

const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');

function emptyForm() {
    const f = {};
    FEE_FIELDS.forEach((field) => {
        f[field.key] = field.type === 'bool' ? false : '';
    });
    f.crb5c_feeactive = true; // sensible default
    return f;
}

export default {
    name: 'ProgrammeFees',
    components: { Icon },
    data() {
        return {
            feeFields: FEE_FIELDS,
            fees: [],
            loadingFees: false,
            form: emptyForm(),
            // excel import
            excelFile: null,
            excelFileName: '',
            excelColumns: [],
            excelRows: [],
            mapping: {},
            // staging
            draftRows: [],
            draftSeq: 0,
            isSubmitting: false,
            // which fee fields get uploaded (user can trim these before confirming)
            include: FEE_FIELDS.reduce((acc, f) => { acc[f.key] = true; return acc; }, {}),
            feeTableFields: [
                { key: 'crb5c_description', label: 'Description' },
                { key: 'crb5c_amt', label: 'Amount', formatter: (v) => this.formatMoney(v) },
                { key: 'crb5c_subsidypercentage', label: 'Subsidy %', formatter: (v) => (v || v === 0 ? `${v}%` : '—') },
                { key: 'flags', label: 'Applies to' },
                { key: 'crb5c_feeactive', label: 'Status' },
            ],
        };
    },
    computed: {
        columnOptions() {
            return [{ value: null, text: '— none —' }, ...this.excelColumns.map((c) => ({ value: c, text: c }))];
        },
        selectedValidCount() {
            return this.draftRows.filter((r) => r._selected && this.rowValid(r) && r._status !== 'done').length;
        },
        // fee fields the user has chosen to upload (Description is always required)
        includedFields() {
            return this.feeFields.filter((f) => this.include[f.key]);
        },
        allSelected: {
            get() {
                const valid = this.draftRows.filter((r) => this.rowValid(r));
                return valid.length > 0 && valid.every((r) => r._selected);
            },
            set(v) {
                this.draftRows.forEach((r) => { if (this.rowValid(r)) r._selected = v; });
            },
        },
        someSelected() {
            const valid = this.draftRows.filter((r) => this.rowValid(r));
            const sel = valid.filter((r) => r._selected).length;
            return sel > 0 && sel < valid.length;
        },
    },
    mounted() {
        this._active = true;
        this.initLoad();
    },
    beforeDestroy() {
        // the admin shell briefly unmounts the router-view while it loads data;
        // stop any in-flight readiness wait so we don't fetch on a dead instance.
        this._active = false;
    },
    methods: {
        formatMoney(v) {
            // Amounts are stored as whole numbers in Dataverse; show thousands
            // separators and only surface decimals if a value happens to have them.
            if (v === '' || v === null || v === undefined || Number.isNaN(Number(v))) return '—';
            return `$${Number(v).toLocaleString('en-SG', { maximumFractionDigits: 2 })}`;
        },
        coerceBool(v) {
            if (typeof v === 'boolean') return v;
            if (v === null || v === undefined) return false;
            return ['1', 'true', 'yes', 'y', 'x', '✓', 'checked', 'active', 't'].includes(String(v).trim().toLowerCase());
        },
        coerceNumber(v) {
            if (v === '' || v === null || v === undefined) return '';
            const n = Number(String(v).replace(/[$,%\s]/g, ''));
            return Number.isNaN(n) ? '' : n;
        },
        rowValid(row) {
            return !!(row.crb5c_description && String(row.crb5c_description).trim());
        },
        async initLoad() {
            // On a hard refresh the router-view renders before the shared api client
            // is created and authenticated (main.js commits `modifyApi` then loads the
            // admin session). Wait for both before the first fetch so we don't fire an
            // unauthenticated request that fails with a "failed to load" toast.
            this.loadingFees = true;
            let tries = 0;
            while (this._active && (!this.$store.state.api || !this.$store.state.loadedAdminDashboard) && tries < 120) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 150));
                tries += 1;
            }
            if (this._active) await this.loadFees();
            else this.loadingFees = false;
        },
        async loadFees() {
            this.loadingFees = true;
            try {
                const { data } = await this.$store.state.api.get('/programme-fees');
                this.fees = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error('Failed to load programme fees:', error);
                this.$bvToast.toast('Failed to load programme fees.', { title: 'Error', variant: 'danger', solid: true });
                this.fees = [];
            } finally {
                this.loadingFees = false;
            }
        },
        addManualToPreview() {
            if (!this.form.crb5c_description) return;
            const row = {};
            FEE_FIELDS.forEach((f) => {
                let val = this.form[f.key];
                if (f.type === 'number') val = this.coerceNumber(val);
                if (f.type === 'bool') val = !!val;
                row[f.key] = val;
            });
            this.draftRows.push({ ...row, _id: this.draftSeq++, _selected: true, _source: 'manual', _status: 'pending', _error: '' });
            this.form = emptyForm();
        },
        onExcelFile(file) {
            if (!file) {
                this.excelColumns = [];
                this.excelRows = [];
                return;
            }
            this.excelFileName = file.name;
            const reader = new FileReader();
            reader.onload = (ev) => {
                try {
                    const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                    this.excelRows = rows;
                    this.excelColumns = rows.length ? Object.keys(rows[0]) : [];
                    this.autoMap();
                } catch (error) {
                    console.error('Failed to parse Excel:', error);
                    this.$bvToast.toast('Could not read that file.', { title: 'Error', variant: 'danger', solid: true });
                }
            };
            reader.readAsArrayBuffer(file);
        },
        autoMap() {
            const mapping = {};
            const normCols = this.excelColumns.map((c) => ({ raw: c, n: norm(c) }));
            const used = new Set();
            FEE_FIELDS.forEach((f) => { mapping[f.key] = null; });
            // Pass 1: exact match (label or synonym). A column is claimed by one field only.
            FEE_FIELDS.forEach((field) => {
                const targets = [norm(field.label), ...field.synonyms];
                const hit = normCols.find((c) => !used.has(c.raw) && targets.includes(c.n));
                if (hit) { mapping[field.key] = hit.raw; used.add(hit.raw); }
            });
            // Pass 2: fuzzy "contains" only for fields still unmapped, on still-unused columns.
            FEE_FIELDS.forEach((field) => {
                if (mapping[field.key]) return;
                const targets = [norm(field.label), ...field.synonyms];
                const hit = normCols.find((c) => !used.has(c.raw) && targets.some((t) => c.n.includes(t) || t.includes(c.n)));
                if (hit) { mapping[field.key] = hit.raw; used.add(hit.raw); }
            });
            this.mapping = mapping;
        },
        loadExcelRowsToPreview() {
            if (!this.mapping.crb5c_description) return;
            let added = 0;
            this.excelRows.forEach((xrow) => {
                const row = {};
                FEE_FIELDS.forEach((f) => {
                    const col = this.mapping[f.key];
                    // Default an unmapped "Active" flag to true (matches the manual
                    // form) so imported tiers aren't silently created inactive and
                    // hidden from the CIP preset selector. Other flags default false.
                    const boolDefault = f.key === 'crb5c_feeactive';
                    let val = col ? xrow[col] : (f.type === 'bool' ? boolDefault : '');
                    if (f.type === 'number') val = this.coerceNumber(val);
                    if (f.type === 'bool') val = this.coerceBool(val);
                    row[f.key] = val;
                });
                // skip completely blank rows
                if (!this.rowValid(row)) return;
                this.draftRows.push({ ...row, _id: this.draftSeq++, _selected: true, _source: 'excel', _status: 'pending', _error: '' });
                added += 1;
            });
            this.$bvToast.toast(`${added} row(s) added to preview.`, { title: 'Imported', variant: 'success', solid: true });
            // reset the file picker so a re-upload triggers change
            this.excelFile = null;
            this.excelColumns = [];
            this.excelRows = [];
        },
        exportToExcel() {
            // Export current fees using the same headers the importer understands,
            // so the file round-trips into another environment.
            if (!this.fees.length) return;
            const rows = this.fees.map((fee) => {
                const r = {};
                FEE_FIELDS.forEach((f) => {
                    let v = fee[f.key];
                    if (f.type === 'bool') v = v ? 'Yes' : 'No';
                    if (v === undefined || v === null) v = '';
                    r[f.label] = v;
                });
                return r;
            });
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Programme Fees');
            const stamp = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `programme-fees-${stamp}.xlsx`);
        },
        removeDraft(index) {
            this.draftRows.splice(index, 1);
        },
        clearDrafts() {
            this.draftRows = this.draftRows.filter((r) => r._status === 'creating');
        },
        async confirmCreate() {
            const toCreate = this.draftRows.filter((r) => r._selected && this.rowValid(r) && r._status !== 'done');
            if (!toCreate.length) return;
            this.isSubmitting = true;
            let ok = 0;
            let fail = 0;
            for (const row of toCreate) {
                row._status = 'creating';
                try {
                    // only upload the fields the user kept; coerce edited cell values
                    const payload = {};
                    this.includedFields.forEach((f) => {
                        let v = row[f.key];
                        if (f.type === 'number') v = this.coerceNumber(v);
                        if (f.type === 'bool') v = this.coerceBool(v);
                        payload[f.key] = v;
                    });
                    payload.crb5c_description = row.crb5c_description; // always required
                    const { data } = await this.$store.state.api.post('/programme-fees', payload);
                    if (data && data.error) throw new Error(typeof data.error === 'string' ? data.error : 'Create failed');
                    row._status = 'done';
                    ok += 1;
                } catch (error) {
                    console.error('Create fee failed:', error);
                    row._status = 'error';
                    row._error = error?.response?.data?.error || error?.message || 'Failed to create';
                    fail += 1;
                }
            }
            this.isSubmitting = false;
            if (ok) {
                this.$bvToast.toast(`${ok} programme fee(s) created${fail ? `, ${fail} failed` : ''}.`, { title: fail ? 'Partial success' : 'Success', variant: fail ? 'warning' : 'success', solid: true });
            } else if (fail) {
                this.$bvToast.toast(`All ${fail} row(s) failed to create.`, { title: 'Error', variant: 'danger', solid: true });
            }
            // drop created rows, keep failures for retry
            this.draftRows = this.draftRows.filter((r) => r._status !== 'done');
            await this.loadFees();
        },
    },
};
</script>

<style scoped>
.dsg-count-pill {
    background-color: var(--dsg-secondary);
    color: #fff;
    font-size: 0.85rem;
    padding: 0.35rem 0.75rem;
}
.dsg-switch {
    margin-top: 0.35rem;
}

/* small applies-to tags */
.dsg-tag {
    display: inline-block;
    padding: 0.1rem 0.5rem;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 700;
    margin: 0 0.2rem 0.2rem 0;
    background: #eef0f4;
    color: #555;
}
.dsg-tag-cip { background: rgba(124, 53, 159, 0.12); color: var(--dsg-secondary); }
.dsg-tag-transport { background: rgba(23, 162, 184, 0.14); color: #0f7686; }

.dsg-pill-active { background: rgba(40, 167, 69, 0.14); color: #1e7e34; }
.dsg-pill-inactive { background: rgba(220, 53, 69, 0.12); color: #b02a37; }

/* field-include selector */
.dsg-include {
    background: #faf9fc;
    border: 1px solid var(--dsg-border);
    border-radius: 10px;
    padding: 0.75rem 1rem;
}
.dsg-include-label {
    display: block;
    font-weight: 700;
    font-size: 0.8rem;
    color: var(--dsg-primary);
    margin-bottom: 0.5rem;
}
.dsg-include-chk {
    font-size: 0.85rem;
    margin-bottom: 0.35rem;
}

/* editable preview grid */
.dsg-editable-wrap {
    overflow-x: auto;
    border: 1px solid var(--dsg-border);
    border-radius: var(--dsg-radius);
}
.dsg-edit-table {
    width: 100%;
    margin: 0;
    border-collapse: collapse;
    font-size: 0.88rem;
}
.dsg-edit-table thead th {
    background-color: var(--dsg-primary);
    color: #fff;
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding: 0.7rem 0.6rem;
    white-space: nowrap;
    vertical-align: middle;
}
.dsg-edit-table tbody td {
    padding: 0.4rem 0.6rem;
    border-top: 1px solid var(--dsg-border);
    vertical-align: middle;
}
.dsg-th-tight { width: 1%; white-space: nowrap; }
.dsg-cell-input { min-width: 120px; }
.dsg-row-invalid { background: rgba(220, 53, 69, 0.05); }

/* excel column mapping */
.dsg-map-title { font-weight: 700; color: var(--dsg-primary); }
.dsg-map-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
}
.dsg-map-row {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    align-items: center;
    gap: 0.75rem;
}
.dsg-map-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #4a4358;
}
</style>
